/* sts-milestones.js — the Routine milestones arithmetic, in one place (v7).
 *
 * Handoff "STS Website Rebuild v7" (2026-10-19) §1. Routine milestones replace
 * the v6 Loyalty milestones: instead of a free product every $300 of anything,
 * the shopper unlocks Dr. Baumann's full-size picks for their skin type and
 * sensitivity level at $150, $400, $750, $1,100, $1,500 and $2,000 of spend on
 * products right for their skin type, then the line starts over.
 *
 * This file holds NO data and fetches nothing. The Skin Profile block, the
 * homepage box, the rewards page card and the cart all ask it the same
 * questions so they can never disagree about a number:
 *
 *   var M = window.STS.milestones;
 *   M.tiers('150, 400, 750')        -> [150, 400, 750]   (the theme setting / metafield)
 *   M.level('DSNT', ['S1','S3'])    -> 'S3'              (which list: 1.4, 1.9)
 *   M.listKey('DSNT', ['S1'])       -> 'dsnt-s1'         (the metaobject handle)
 *   M.ladder(tiers, giftCount)      -> the shopper's own tiers (4, 5 or 6)
 *   M.hits(ladder, spend)           -> how many tiers this cycle's spend reached
 *   M.fill(ladder, spend)           -> % of the line to fill (1.6: ends ON a reached tier)
 *   M.at(i, n)                      -> % from the left of tier i's dot
 *   M.toNext(ladder, spend)         -> dollars to the next tier, 0 when all reached
 *   M.amounts(doc, gifts)           -> each tier's amount: the document's frozen cycle amounts first
 *   M.counts(line, type)            -> does this cart line move the bar (1.5)
 *   M.cartSpend(lines, type)        -> dollars of a cart that count
 *
 * The backend keeps the shopper's spend THIS CYCLE as its own number (1.10:
 * "don't work it out from lifetime spend and the last tier, because the last
 * tier can change on a retake"), so nothing here derives a cycle from a total.
 * Amounts are DOLLARS, as in the customer metafield and the theme setting.
 */
(function (w) {
  'use strict';

  var STS = (w.STS = w.STS || {});
  if (STS.milestones) return;

  /* 1.2, the accountant-approved sheet. Only a fallback: the shop metafield
     sts.loyalty_config.tiers, then the theme setting, come first. */
  var DEFAULT_TIERS = [150, 400, 750, 1100, 1500, 2000];
  var MAX_TIERS = 6;

  /* sts.loyalty_config's rules, Max's answers (2026-10-20) as defaults, engine
     spec §3.2 names. The backend enforces them; the theme reads them for copy. */
  var CONFIG = {
    min_paid_orders: 0,              // 0/1 = spend alone unlocks the first gift
    cycle_reset: 'cycle',            // lifetime: starts over only after the last tier
    cycle_months: 0,
    missing_level_default: '',       // 7.1-105 (Luis 2026-10-29): no guess; the quiz always gives the level
    gift_swap: true,                 // out of stock: the list's spare picks first, then later tiers' gifts, within the cost rule
    low_stock_threshold: 3,
    cost_rule_per_100: 20,           // a gift costs at most $20 per $100 of its tier (handoff 1.8)
    cost_rate: 0.5                   // cost = 50% of the sheet value
  };
  function cfg(c) {
    var out = {}, k;
    for (k in CONFIG) if (Object.prototype.hasOwnProperty.call(CONFIG, k)) out[k] = (c && c[k] != null) ? c[k] : CONFIG[k];
    return out;
  }

  /* The SAME rule as snippets/sts-milestone-list.liquid, so a guest's ladder
     and a signed-in customer's can never differ: comma-separated, "$"
     ignored, and only amounts that are positive and higher than the one
     before are kept, at most six (the metaobject has six slots). A
     thousands separator splits a number ("750, 1,100" keeps 750 and drops
     the 1 and the 100), which is why the setting's info text forbids it.
     None left: the approved defaults. */
  function tiers(raw) {
    var src = Array.isArray(raw) ? raw : String(raw == null ? '' : raw).replace(/\$/g, '').split(',');
    var out = [], prev = 0, i, n;
    for (i = 0; i < src.length && out.length < MAX_TIERS; i++) {
      n = Number(String(src[i]).trim());
      if (n > prev) { out.push(n); prev = n; }
    }
    return out.length ? out : DEFAULT_TIERS.slice();
  }

  var TYPE_RE = /^[DO][SR][PN][TW]$/;

  /* 1.4 / 1.9: one list per skin type and sensitivity level. R types have one
     list (R1). S types have four, S1 to S4; the quiz can return several
     sensitivities, and the list is the HIGHEST of them — the gentlest gifts —
     the same "keep only the highest" rule layout/theme.liquid already reports
     to GA4. The level always comes from the quiz (7.1-105, Luis 2026-10-29:
     "The Quiz always gives"): an S type with no level on record has no list
     ('') until a quiz gives one; there is no S2 guess. `fallback` is only
     honoured when the caller passes a REAL level it already knows (the
     backend document's own `level`). */
  function highest(sens) {
    var list = Array.isArray(sens) ? sens : String(sens || '').split(/[^A-Za-z0-9]+/);
    var best = 0, i, m;
    for (i = 0; i < list.length; i++) {
      m = /^S([1-4])$/.exec(String(list[i]).trim().toUpperCase());
      if (m && +m[1] > best) best = +m[1];
    }
    return best;
  }
  function levelSource(type, sens) {
    var t = String(type || '').toUpperCase();
    if (!TYPE_RE.test(t)) return '';
    return t.charAt(1) === 'R' ? 'type' : highest(sens) ? 'quiz' : '';
  }
  function level(type, sens, fallback) {
    var t = String(type || '').toUpperCase();
    if (!TYPE_RE.test(t)) return '';
    if (t.charAt(1) === 'R') return 'R1';
    var best = highest(sens);
    var fb = /^S[1-4]$/.test(String(fallback || '').toUpperCase()) ? String(fallback).toUpperCase() : '';
    return best ? 'S' + best : fb;
  }

  function listKey(type, sens, fallback) {
    var l = level(type, sens, fallback);
    return l ? String(type).toLowerCase() + '-' + l.toLowerCase() : '';
  }

  /* 1.4: shorter lists end at their last gift, and the cycle restarts there.
     A list never shows more tiers than it has gifts, and never "To be
     announced" slots. */
  function ladder(all, giftCount) {
    var t = tiers(all);
    var n = Math.floor(Number(giftCount) || t.length);
    return t.slice(0, Math.max(1, Math.min(n, t.length)));
  }

  function hits(t, spend) {
    var s = Number(spend) || 0, n = 0;
    for (var i = 0; i < t.length; i++) if (s >= t[i]) n++;
    return n;
  }

  /* 1.6 "The fill ends exactly on reached tiers": the dots sit at the CENTRE
     of n equal columns, (i + .5) / n, and the fill runs to the last reached
     dot plus the share of the way to the next one. Prototype v7, word for
     word: hitCount ? hitCount - .5 + fr : .5 * fr, over n. */
  function at(i, n) {
    return Math.round(((i + 0.5) / n) * 1000) / 10;
  }

  function fill(t, spend) {
    var n = t.length, s = Math.max(0, Number(spend) || 0);
    if (!n) return 0;
    var h = hits(t, s);
    if (h >= n) return 100;
    var prev = h ? t[h - 1] : 0;
    var fr = (s - prev) / (t[h] - prev);
    return Math.round(((h ? h - 0.5 + fr : 0.5 * fr) / n) * 1000) / 10;
  }

  function toNext(t, spend) {
    var h = hits(t, spend);
    return h < t.length ? Math.round((t[h] - (Number(spend) || 0)) * 100) / 100 : 0;
  }

  /* 1.5 What counts. A line moves the bar when its product is approved for the
     shopper's type (the "Fits [type]" check: the type is on the product's
     list), it was paid for, and it is for the shopper. Not: a gift (a claimed
     milestone, a $0 gift-with-purchase), a gift card (the Loopz card is a
     regular product, SKUs STS-LOOPZ-GC-*, DECISIONS 2026-10-19), or a line
     bought for a friend ("Shop by any type" / the friend routine put
     `_sts_for_type` and `_sts_src: friend_routine` on it). `price` is the
     line after ALL its discounts, before tax and shipping.
     `line` is {approved: ['DSNT', ...], price: dollars, gift_card, sku, properties}. */
  var GC_SKU = /^STS-LOOPZ-GC-/i;
  function counts(line, type) {
    if (!line || !TYPE_RE.test(String(type || ''))) return false;
    var p = line.properties || {};
    if (line.gift_card || GC_SKU.test(String(line.sku || '')) || p._sts_reward || p._sts_preview) return false;
    if (!(Number(line.price) > 0)) return false;
    if (p._sts_src === 'friend_routine') return false;
    if (p._sts_for_type && String(p._sts_for_type).toUpperCase() !== type) return false;
    var ap = line.approved || [];
    for (var i = 0; i < ap.length; i++) if (String(ap[i]).toUpperCase() === type) return true;
    return false;
  }

  function cartSpend(lines, type) {
    var sum = 0;
    for (var i = 0; i < (lines || []).length; i++) {
      if (counts(lines[i], type)) sum += Number(lines[i].price) || 0;
    }
    return Math.round(sum * 100) / 100;
  }

  /* 1.8 and DECISIONS: a gift may stand in for a tier only if its cost (sheet
     value x cost_rate) is at most cost_rule_per_100 per $100 of the tier. */
  function costOk(value, amount, c) {
    var k = cfg(c);
    return (Number(value) || 0) * Number(k.cost_rate) <= (Number(amount) || 0) * Number(k.cost_rule_per_100) / 100 + 1e-9;
  }

  /* The out-of-stock swap, the engine's order (swap.ts pickGift, §8.4,
     DECISIONS 2026-10-21) and the same rule as
     snippets/sts-milestone-list.liquid: the list's SPARE picks (`alternates`)
     first, in the list's order, then the LATER tiers' gifts in tier order —
     so one missing gift never shifts the ladder. Never the tier's own gift,
     a variant in `taken` or a product type in `types` (both: what is
     already claimed or reserved this cycle, and the gifts of the tiers
     below it). */
  function swapCandidates(gifts, alternates, i, taken, types) {
    var no = {}, ty = {}, seen = {}, out = [];
    var id = function (g) { return String((g && (g.variant_id || g.handle)) || '').replace(/^.*\//, ''); };
    (taken || []).forEach(function (v) { no[String(v).replace(/^.*\//, '')] = 1; });
    (types || []).forEach(function (t) { if (t) ty[String(t).trim().toLowerCase()] = 1; });
    no[id((gifts || [])[i])] = 1;
    (alternates || []).concat((gifts || []).slice(i + 1)).forEach(function (g) {
      var k = id(g);
      if (!g || !k || no[k] || seen[k]) return;
      seen[k] = 1;
      if (g.type && ty[String(g.type).trim().toLowerCase()]) return;
      out.push(g);
    });
    return out;
  }

  /* A tier's own gift is served unless it is out, low, breaks the cost rule,
     or an earlier tier this cycle already took that same VARIANT (every
     spare was out and it swapped onto this tier's gift). A product type
     match never pushes a tier off its own gift (engine, 2026-10-21). */
  function ownTaken(g, taken) {
    var k = String((g && (g.variant_id || g.handle)) || '').replace(/^.*\//, '');
    return !!k && (taken || []).some(function (v) { return String(v).replace(/^.*\//, '') === k; });
  }

  /* "Brand Title" without doubling a brand the title already starts with
     (engine swap.ts giftName): never "Replenix Replenix Gly-Sal". */
  function giftName(brand, title) {
    var b = String(brand == null ? '' : brand).trim(), t = String(title == null ? '' : title).trim();
    if (!b) return t;
    if (!t) return b;
    var lb = b.toLowerCase(), lt = t.toLowerCase();
    return lt === lb || lt.indexOf(lb + ' ') === 0 ? t : b + ' ' + t;
  }

  /* The first of `candidates` (swapCandidates) that is {available}, above
     low_stock_threshold and passes costOk for this tier, or null (keep the
     original: "Ships when back in stock"). Off when gift_swap is. */
  function pickSwap(candidates, amount, c) {
    var k = cfg(c);
    if (!k.gift_swap) return null;
    for (var i = 0; i < (candidates || []).length; i++) {
      var x = candidates[i];
      if (x && x.available && !(Number(x.stock) <= Number(k.low_stock_threshold)) && costOk(x.value, amount, k)) return x;
    }
    return null;
  }

  /* The whole ladder as the engine would give it, lowest tier first (the
     JS twin of snippets/sts-milestone-list.liquid's swap, for the staff
     simulator and the tests): a tier whose gift is out (o.out(g, i), by
     default unavailable or at/below low_stock_threshold; a swap candidate
     is asked with i = -1) or whose variant
     a tier below already took swaps to pickSwap(swapCandidates(...)) with
     `swapped_from` (the original's name), else keeps its gift marked
     `backorder`. Each settled gift's variant and type are then taken for
     the tiers above. o.taken / o.types: claimed or reserved this cycle;
     o.done: the indexes of those tiers (their snapshot, never re-picked). */
  function settle(gifts, alternates, o) {
    o = o || {};
    var k = cfg(o.config);
    var taken = (o.taken || []).slice(), types = (o.types || []).slice();
    var out = o.out || function (g) { return !g || g.available === false || (g.stock != null && Number(g.stock) <= Number(k.low_stock_threshold)); };
    var copy = function (a, b) { var r = {}, f; for (f in a) if (Object.prototype.hasOwnProperty.call(a, f)) r[f] = a[f]; for (f in b) if (Object.prototype.hasOwnProperty.call(b, f)) r[f] = b[f]; return r; };
    return (gifts || []).map(function (g, i) {
      if ((o.done || []).indexOf(i) > -1) return g;
      var x = g;
      if (out(g, i) || ownTaken(g, taken)) {
        var sub = pickSwap(swapCandidates(gifts, alternates, i, taken, types).filter(function (c) { return !out(c, -1); }), g && g.amount, k);
        x = sub ? copy(sub, { amount: g.amount, swapped_from: giftName(g.brand, g.title) || 'your original gift', backorder: false })
          : copy(g, { backorder: true });
      }
      if (!x.backorder) {
        taken.push(String(x.variant_id || x.handle || ''));
        if (x.type) types.push(x.type);
      }
      return x;
    });
  }

  /* The document's `swapped_from` is the original gift's brand + title (the
     engine, 10-20). An older document wrote its variant id: never print a
     number or a GID as a product name. */
  function swappedFrom(v) {
    var s = String(v == null ? '' : v).trim();
    if (!s) return '';
    return /^\d+$/.test(s) || /^gid:\/\//.test(s) ? 'your original gift' : s;
  }

  /* A list from the theme's built-in copy (snippets/sts-milestone-gifts.liquid,
     printed into a page as {lists, products, tiers}) for a shopper the server
     cannot resolve: a typed guest, or the staff simulator's shopper. Same
     shape as snippets/sts-milestone-list.liquid; no pictures (the copy holds
     none), and links by handle. */
  function fromTable(table, type, sens, fallback) {
    var key = listKey(type, sens, fallback);
    var t = table || {};
    var idx = [], alt = [];
    String(t.lists || '').split(';').some(function (l) {
      var kv = l.split('=');
      if (kv[0] !== key) return false;
      var parts = String(kv[1] || '').split('/');
      idx = parts[0].split(',').map(Number);
      alt = parts[1] ? parts[1].split(',').map(Number) : [];
      return true;
    });
    var rows = String(t.products || '').split('^');
    var amounts = tiers(t.tiers);
    var gift = function (pi, amount) {
      var p = String(rows[pi] || '').split('|');
      return {
        amount: amount, handle: p[0] || '', variant_id: Number(p[1]) || null, value: Number(p[2]) || 0,
        type: p[3] || '', brand: p[4] || '', title: p[5] || '', image: '',
        url: p[0] ? '/products/' + p[0] + (p[1] ? '?variant=' + p[1] : '') : '', available: true
      };
    };
    return {
      key: key, type: String(type || '').toUpperCase(), level: level(type, sens, fallback), level_source: levelSource(type, sens), source: 'theme',
      gifts: idx.slice(0, amounts.length).map(function (pi, i) { return gift(pi, amounts[i]); }),
      /* The list's other sheet picks, for the out-of-stock swap. */
      alternates: alt.map(function (pi) { return gift(pi, null); })
    };
  }

  /* ── The backend's v2 document (engine spec §3.3, customer metafield
     sts.loyalty) ─────────────────────────────────────────────────────────────
     Read the same way by the Skin Profile block, the homepage / rewards box
     and the cart, so a state can never read one way in one place and another
     way in the next. The engine writes it and sends every tier's `state`;
     these only fill what a missing or partial document leaves out. No
     document at all (DECISIONS: no backfill) is $0, cycle 1, all locked. */
  var DAY = 864e5;
  function v2(doc) { return doc && Number(doc.v) === 2 ? doc : null; }

  /* Why the shopper sees no ladder, or '' when they are in the program:
     holdout (the staggered rollout, §11), excluded (staff, doctor routine),
     blocked (fraud review), no_skin_type (take the quiz). An unknown reason
     fails closed, as excluded. */
  function reason(doc) {
    var x = v2(doc);
    if (!x) return '';
    if (x.holdout === true) return 'holdout';
    if (x.eligible !== false) return '';
    return /^(holdout|excluded|blocked|no_skin_type)$/.test(x.reason) ? x.reason : 'excluded';
  }

  /* One tier: the engine's state when it sent one, else derived (the gate
     holds tier 1). An unlocked gift past its claim date reads expired even
     when the document has not caught up yet: the rule is 180 days. */
  var STATES = /^(locked|unlocked|reserved|claimed|expired|relocked)$/;
  function tierState(r, i, amount, spend, gated, now) {
    var s = r && STATES.test(r.state) ? r.state : (Number(spend) || 0) >= amount && !(i === 0 && gated) ? 'unlocked' : 'locked';
    var exp = r && Date.parse(r.expires_at || '');
    if (s === 'unlocked' && exp && exp < (now || Date.now())) s = 'expired';
    return s;
  }

  /* Paid orders still needed for the first gift: 0 unless the doc's gate
     (the engine copies the config) asks for 2+ and is not met. */
  function gateLeft(gate) {
    var g = gate || {};
    var min = Number(g.min_paid_orders != null ? g.min_paid_orders : CONFIG.min_paid_orders) || 0;
    if (g.met !== false || min < 2) return 0;
    return Math.max(1, min - (Number(g.paid_orders) || 0));
  }

  /* A dated cycle: only when the doc's config sets a cycle_reset other
     than 'cycle' (the default is lifetime spend, no end date). */
  function timed(doc) {
    var x = v2(doc) || {};
    var r = x.config && x.config.cycle_reset != null ? x.config.cycle_reset : CONFIG.cycle_reset;
    return String(r) !== 'cycle' && !!(x.cycle && Date.parse(x.cycle.ends_at || ''));
  }

  /* The cycle as the copy needs it. `left` = gateLeft. `waiting` = the last
     cycle closed and the next opens with the next order (§6.8). `endsAt` /
     `ending` (30 days or less): only when timed(). */
  function cycle(doc, now) {
    var x = v2(doc) || {};
    var c = x.cycle || {};
    var t = now || Date.now();
    var dated = timed(doc);
    var ends = dated ? Date.parse(c.ends_at) : 0;
    var raw = Number(c.spend_in_cycle) || 0;
    var r2 = function (n) { return Math.round(Math.max(0, Number(n) || 0) * 100) / 100; };
    return {
      index: Number(c.index) || 1,
      spend: r2(raw),
      deficit: r2(Number(c.deficit) || -raw),
      carryIn: r2(c.carry_in),
      carryNext: r2(c.carry_next),
      waiting: !!x.cycle && !c.started_at && (Number(c.index) > 1 || Number(c.carry_in) > 0),
      endsAt: dated ? c.ends_at : '',
      ending: ends > t && ends - t <= 30 * DAY,
      left: gateLeft(x.gate),
      levelDefault: x.level_source === 'default'
    };
  }

  /* The next tier still to earn, from the tiers' states: the first locked
     or relocked one. Dollars to go include a refund deficit (§6.4: spend
     can go below $0 and is refilled first); a v2 `doc` whose `next` names
     the same tier supplies its own. {i: -1} when nothing is left. */
  function nextTier(states, amounts, spend, deficit, doc) {
    var en = v2(doc) && doc.next;
    for (var i = 0; i < states.length; i++) {
      if (states[i] === 'locked' || states[i] === 'relocked') {
        /* The engine's own `next` when it names this tier: between cycles it
           counts the carry the next order opens the cycle with. */
        if (en && Number(en.i) === i && en.to_go != null && isFinite(Number(en.to_go))) return { i: i, toGo: Math.max(0, Number(en.to_go)) };
        return { i: i, toGo: Math.max(0, Math.round((amounts[i] - (Number(spend) || 0) + (Number(deficit) || 0)) * 100) / 100) };
      }
    }
    return { i: -1, toGo: 0 };
  }

  /* Each tier's amount (engine §6.10): the CYCLE's frozen amounts from the
     document (each reward's `amount`, else its config.tiers, which the
     engine writes as the open cycle's tier_amounts), so a config change
     mid-cycle never moves a shopper's line. Only a guest or an account with
     no document reads the list's (the shop config's) amounts. One entry per
     tier the document or the list has, whichever is more. */
  function amounts(doc, gifts) {
    var x = v2(doc) || {};
    var rw = (x.cycle && x.cycle.rewards) || [];
    var ct = (x.config && x.config.tiers) || [];
    var g = gifts || [];
    var n = Math.max(rw.length, g.length);
    var out = [];
    for (var i = 0; i < n; i++) {
      out.push(Number(rw[i] && rw[i].amount) || Number(ct[i]) || Number(g[i] && g[i].amount) || 0);
    }
    return out;
  }

  /* The cycle's extra lines, in the order they show, each {k, text}.
     `fmt` formats dollars, `date` an ISO date; `toNext` and `first` (is tier
     1 still to come) are the surface's own numbers. */
  function notes(c, o) {
    var out = [];
    var m = o.fmt, sub = function (s, map) { return s.replace(/\{(\w+)\}/g, function (a, k) { return map[k] != null ? map[k] : a; }); };
    if (c.left && o.first) out.push({ k: 'gate', text: c.left === 1 ? '1 more order to unlock your first gift.' : c.left + ' more orders to unlock your first gift.' });
    if (c.deficit > 0) out.push({ k: 'deficit', text: sub('After a refund this cycle is {X} behind. Your next purchases make that up first, then count toward your next gift.', { X: m(c.deficit) }) });
    if (c.waiting) {
      out.push({ k: 'carry', text: c.carryIn > 0 ? sub('Your new cycle starts with your next order, and the extra {X} from your last one already counts toward it.', { X: m(c.carryIn) })
        : 'Your new cycle starts with your next order.' });
    } else if (c.carryIn > 0) {
      out.push({ k: 'carry', text: sub('Includes {X} carried over from your last cycle.', { X: m(c.carryIn) }) });
    }
    if (c.ending && o.toNext > 0) out.push({ k: 'ending', text: sub('Your cycle ends on {DATE}. {X} more by then unlocks your next gift.', { DATE: o.date(c.endsAt), X: m(o.toNext) }) });
    return out;
  }


  STS.milestones = {
    DEFAULT_TIERS: DEFAULT_TIERS,
    CONFIG: CONFIG,
    cfg: cfg,
    levelSource: levelSource,
    costOk: costOk,
    pickSwap: pickSwap,
    swapCandidates: swapCandidates,
    ownTaken: ownTaken,
    settle: settle,
    giftName: giftName,
    swappedFrom: swappedFrom,
    amounts: amounts,
    fromTable: fromTable,
    reason: reason,
    tierState: tierState,
    cycle: cycle,
    gateLeft: gateLeft,
    timed: timed,
    nextTier: nextTier,
    notes: notes,
    tiers: tiers,
    level: level,
    listKey: listKey,
    ladder: ladder,
    hits: hits,
    at: at,
    fill: fill,
    toNext: toNext,
    counts: counts,
    cartSpend: cartSpend
  };
}(window));
